/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag Fragment
 */
import React from 'react';

import { css, jsx } from '@compiled/react';

import Link from '@atlaskit/link';
import Tag from '@atlaskit/tag';
import { token } from '@atlaskit/tokens';

import { Assignee as AssigneeComponent } from '../../../../../../../common/ui/jira-assignee';
import { getStatus } from '../../../../../../../common/ui/jira-status';
import { getJiraEntityIcon } from '../../../../../../../common/ui/product-entity-icon';
// TODO: extract this type out into its own file so it can be shared
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import type { JiraIssueStatusAndColorName } from '../index';

export type SearchJiraIssueTableRowProps = {
	/** Issue type icon URL (or null to fall back to type-based icon). */
	iconUrl: string | null | undefined;
	/** The Jira result type (e.g. "issue", "epic"). */
	type: string;
	/** Resolved issue key, e.g. "PUP-002". */
	issueKey: string | undefined;
	/** Fully-qualified URL for the issue. */
	url: string;
	/** Title / summary of the issue. */
	title: string | null | undefined;
	/** Status lozenge data — hydrated asynchronously, may be null while loading. */
	jiraIssueStatus?: JiraIssueStatusAndColorName | null;
	/** Assignee data. */
	assignee?: { name: string; picture: string } | null;
	/** Priority data from JiraPriorityField. */
	priority?: { name: string | null | undefined; iconUrl: string | null | undefined } | null;
	/** Label names from JiraLabelsField.selectedLabelsConnection. */
	labels?: string[];
	/** Called when the user clicks anywhere on the row. */
	onClick?: () => void;
};

const styles = {
	tr: css({
		display: 'table-row',
		height: '40px',
		backgroundColor: token('elevation.surface'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
		cursor: 'pointer',
	}),
	tdWork: css({
		display: 'table-cell',
		verticalAlign: 'middle',
		paddingInlineStart: token('space.100'),
		paddingInlineEnd: token('space.100'),
		// there is a top-level style that has very high-specificty that targets
		// the first cell of any tables and removes padding so we need to override this
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		paddingLeft: `${token('space.100')} !important`,
		overflow: 'hidden',
	}),
	tdWorkInner: css({
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.075'),
		overflow: 'hidden',
	}),
	tdFixed: css({
		display: 'table-cell',
		verticalAlign: 'middle',
		paddingInlineStart: token('space.100'),
		paddingInlineEnd: token('space.100'),
		overflow: 'hidden',
		boxSizing: 'border-box',
		borderInlineStartWidth: token('border.width'),
		borderInlineStartStyle: 'solid',
		borderInlineStartColor: token('color.border'),
		whiteSpace: 'nowrap',
	}),
	tdFixedInner: css({
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
	}),
	link: css({
		flexShrink: 0,
		color: token('color.link'),
		// fontSize: '0.75rem',
		fontWeight: token('font.weight.regular'),
	}),
	iconWrapper: css({
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
	}),
	summary: css({
		flexGrow: 1,
		flexShrink: 1,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	}),
	tdLast: css({
		// there is a top-level style that has very high-specificty that targets
		// the last cell of any tables and removes padding so we need to override this
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		paddingRight: `${token('space.100')} !important`,
	}),
};

// logic copied from /packages/search/search-common/src/utils/highlight-matched-text.tsx
const HIGHLIGHT_START_TAG = '@@@hl@@@';
const HIGHLIGHT_END_TAG = '@@@endhl@@@';
const cleanStartRegex = new RegExp(HIGHLIGHT_START_TAG, 'g');
const cleanEndRegex = new RegExp(HIGHLIGHT_END_TAG, 'g');
// used to remove the special highlighting characters from the Jira issue title that we get back from GQL
const removeSearchHighlighting = (source: string): string =>
	source.replace(cleanStartRegex, '').replace(cleanEndRegex, '');

export const SearchJiraIssueTableRow = ({
	iconUrl,
	type,
	issueKey,
	url,
	title,
	jiraIssueStatus,
	assignee,
	priority,
	labels = [],
	onClick,
}: SearchJiraIssueTableRowProps): React.JSX.Element => {
	const icon = getJiraEntityIcon({ iconUrl, type, isIssue: true, size: 'small' });
	const status =
		jiraIssueStatus != null
			? getStatus(jiraIssueStatus.statusName, jiraIssueStatus.colorName, false)
			: null;
	const cleanTitle = removeSearchHighlighting(title ?? '');

	return (
		<tr css={styles.tr} data-testid="search-jira-issue-table-row" onClick={onClick}>
			{/* Work column: icon + issue key link + summary */}
			<td css={styles.tdWork}>
				<div css={styles.tdWorkInner}>
					<span css={styles.iconWrapper}>{icon}</span>
					{issueKey && (
						<Link href={url} target="_blank" onClick={onClick}>
							{issueKey}
						</Link>
					)}
					<span css={styles.summary}>{cleanTitle}</span>
				</div>
			</td>

			{/* Status column */}
			<td css={styles.tdFixed}>{status || null}</td>

			{/* Assignee column */}
			<td css={styles.tdFixed}>
				<AssigneeComponent user={assignee ?? null} />
			</td>

			{/* Labels column — first label chip (if any) */}
			<td css={styles.tdFixed}>{labels[0] && <Tag text={labels[0]} isRemovable={false} />}</td>

			{/* Priority column — priority icon + name */}
			<td css={[styles.tdFixed, styles.tdLast]}>
				<div css={styles.tdFixedInner}>
					{priority?.iconUrl && (
						<span css={styles.iconWrapper}>
							<img src={priority.iconUrl} alt={priority.name ?? ''} width={16} height={16} />
						</span>
					)}
					{priority?.name && priority.name}
				</div>
			</td>
		</tr>
	);
};
